// ─────────────────────────────────────────────────────────────────────────────
// desktopBackgroundSwatch — resolves a Space's SpaceBackground into a plain
// inline style for a non-interactive preview swatch (no live WebGL context,
// no scoped CSS class dependency). Shared by DesktopSpaceMock (the switcher's
// thumbnail) and DesktopSwipeDragLayer (the full-viewport interactive drag
// preview) so the two never drift on how a background type resolves to a
// color/image/pattern.
//
// For color/image/preset/pattern this is an EXACT reproduction of the CSS
// GUILayout's `::before`/`::after` pseudo-elements paint (see the `.pattern-*`
// rules in GUILayout.scss) — not an approximation — because
// DesktopSwipeDragLayer relies on it to render the incoming pane's actual
// target background during the drag, not a flat approximation that would
// visibly snap to the real thing the moment the drag commits.
// Procedural is the one exception: it stays a live `ProceduralBackground`
// WebGL instance (mounted by the caller), not a style from this function.
// ─────────────────────────────────────────────────────────────────────────────
import type { CSSProperties } from 'react';
import type { SpaceBackground } from '../../../../types/spaces';
import { getGenerator } from '../../backgrounds/proceduralRegistry';
import { resolveBackgroundImageUrl } from '../../../../utils/backgroundImageUtils';
import { presetBackgroundCssVar } from '../../../../constants/spaceBackgrounds';

// Mirrors the `.pattern-*::before` rules in GUILayout.scss exactly — same
// gradients, same tile sizes, same positions. `currentColor` in the SCSS is
// replaced with an explicit color argument here since this swatch has no
// `color` inheritance chain to rely on.
const PATTERN_DEFS: Record<string, {
  backgroundImage: (color: string) => string;
  backgroundSize?: string;
  backgroundPosition?: string;
  backgroundRepeat?: string;
}> = {
  'pattern-checks-sm': {
    backgroundImage: (c) => `conic-gradient(${c} 90deg, transparent 90deg 180deg, ${c} 180deg 270deg, transparent 270deg)`,
    backgroundSize: '20px 20px',
    backgroundPosition: '0 0',
    backgroundRepeat: 'repeat',
  },
  'pattern-checks-md': {
    backgroundImage: (c) => `conic-gradient(${c} 90deg, transparent 90deg 180deg, ${c} 180deg 270deg, transparent 270deg)`,
    backgroundSize: '40px 40px',
    backgroundPosition: '0 0',
    backgroundRepeat: 'repeat',
  },
  'pattern-dots-sm': {
    backgroundImage: (c) => `radial-gradient(circle at center, ${c} 1.5px, transparent 1.5px)`,
    backgroundSize: '15px 15px',
    backgroundRepeat: 'repeat',
  },
  'pattern-dots-md': {
    backgroundImage: (c) => `radial-gradient(circle at center, ${c} 2.5px, transparent 2.5px)`,
    backgroundSize: '25px 25px',
    backgroundRepeat: 'repeat',
  },
  'pattern-grid-sm': {
    backgroundImage: (c) => `linear-gradient(${c} 1px, transparent 1px), linear-gradient(90deg, ${c} 1px, transparent 1px)`,
    backgroundSize: '20px 20px',
  },
  'pattern-grid-md': {
    backgroundImage: (c) => `linear-gradient(${c} 1px, transparent 1px), linear-gradient(90deg, ${c} 1px, transparent 1px)`,
    backgroundSize: '40px 40px',
  },
  'pattern-diagonal-lines-sm': {
    backgroundImage: (c) => `repeating-linear-gradient(45deg, ${c} 0px, ${c} 1px, transparent 1px, transparent 10px)`,
    backgroundSize: 'auto',
  },
  'pattern-horizontal-lines-sm': {
    backgroundImage: (c) => `repeating-linear-gradient(0deg, ${c} 0px, ${c} 1px, transparent 1px, transparent 10px)`,
    backgroundSize: 'auto',
  },
  'pattern-vertical-lines-sm': {
    backgroundImage: (c) => `repeating-linear-gradient(90deg, ${c} 0px, ${c} 1px, transparent 1px, transparent 10px)`,
    backgroundSize: 'auto',
  },
  'pattern-diagonal-stripes-sm': {
    backgroundImage: (c) => `repeating-linear-gradient(45deg, ${c} 0px, ${c} 5px, transparent 5px, transparent 10px)`,
    backgroundSize: 'auto',
  },
  'pattern-triangles-sm': {
    backgroundImage: (c) => [
      `linear-gradient(135deg, ${c} 25%, transparent 25%)`,
      `linear-gradient(225deg, ${c} 25%, transparent 25%)`,
      `linear-gradient(45deg, ${c} 25%, transparent 25%)`,
      `linear-gradient(315deg, ${c} 25%, transparent 25%)`,
    ].join(', '),
    backgroundSize: '20px 20px',
    backgroundPosition: '0 0, 10px 0, 10px -10px, 0 -10px',
    backgroundRepeat: 'repeat',
  },
  'pattern-zigzag-sm': {
    backgroundImage: (c) => [
      `linear-gradient(135deg, ${c} 25%, transparent 25%)`,
      `linear-gradient(225deg, ${c} 25%, transparent 25%)`,
      `linear-gradient(315deg, ${c} 25%, transparent 25%)`,
      `linear-gradient(45deg, ${c} 25%, transparent 25%)`,
    ].join(', '),
    backgroundSize: '20px 20px',
    backgroundPosition: '10px 0, 10px 10px, 0 10px, 0 0',
    backgroundRepeat: 'repeat',
  },
};

export function resolveBackgroundSwatchStyle(
  background?: SpaceBackground | null,
  themeScheme: 'light' | 'dark' = 'light',
): CSSProperties {
  const base: CSSProperties = {
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };

  if (!background) {
    return { ...base, backgroundImage: 'var(--bg-np-1)' };
  }

  if (background.type === 'image') {
    const url = resolveBackgroundImageUrl(background);
    return url ? { ...base, backgroundImage: `url(${url})` } : { ...base, backgroundImage: 'var(--bg-np-1)' };
  }

  if (background.type === 'preset' && background.value) {
    const cssVar = presetBackgroundCssVar(background.value);
    if (cssVar) {
      return { ...base, backgroundImage: cssVar };
    }
  }

  if (background.type === 'color' && background.value) {
    return { ...base, background: background.value };
  }

  if (background.type === 'pattern' && background.value) {
    const def = PATTERN_DEFS[background.value];
    if (def) {
      const patternColor = background.patternColor || '#ffffff';
      const patternBgColor = background.patternBgColor || (themeScheme === 'light' ? '#ffffff' : '#1a1a1a');
      return {
        backgroundColor: patternBgColor,
        backgroundImage: def.backgroundImage(patternColor),
        backgroundSize: def.backgroundSize ?? base.backgroundSize,
        backgroundPosition: def.backgroundPosition ?? base.backgroundPosition,
        backgroundRepeat: def.backgroundRepeat,
      };
    }
  }

  if (background.type === 'procedural') {
    // A live WebGL renderer per preview would mount a real GL context for
    // every desktop shown (switcher thumbnails, drag preview), which quickly
    // exhausts the browser's context cap (see contours.ts etc.). Approximate
    // with a gradient spanning the whole palette instead of a single flat
    // color — every palette's colors[0] is deliberately its darkest/most
    // extreme "ground" tone (deep water, void, etc.), so a flat swatch of
    // just that one color reads as solid dark, nothing like the actual
    // render's much lighter overall blend of all six ramp stops.
    const gen = getGenerator(background.generator ?? 'topo');
    const palette = gen?.palettes.find((p) => p.id === (background.palette ?? gen.defaultPalette));
    const colors = palette?.colors;
    if (colors && colors.length > 0) {
      return { ...base, background: `linear-gradient(160deg, ${colors.join(', ')})` };
    }
    return { ...base, backgroundImage: 'var(--bg-np-1)' };
  }

  // Unknown type or missing data — fall back to default bg.
  return { ...base, backgroundImage: 'var(--bg-np-1)' };
}
